import { TopNav } from "@dashboard/components/AppLayout";
import { DashboardCard } from "@dashboard/components/Card";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import { getApiUrl, getAppPlatformUrl } from "@dashboard/config";
import { postConfigurationUpdate } from "@dashboard/envSettings/services/configurationApi";
import { ExtensionsUrls } from "@dashboard/extensions/urls";
import useNotifier from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import { Box, Button, Input, Spinner, Switch, Text } from "@saleor/macaw-ui-next";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";

interface CurrencyRate {
  code: string;
  rate: string;
}

interface CurrencyConfig {
  base_currency: string;
  multi_currency_enabled: boolean;
  currencies: CurrencyRate[];
}

const defaultConfig: CurrencyConfig = {
  base_currency: "USD",
  multi_currency_enabled: false,
  currencies: [{ code: "CAD", rate: "" }],
};

export const CurrencySettings: React.FC = () => {
  const intl = useIntl();
  const notify = useNotifier();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [config, setConfig] = useState<CurrencyConfig>(defaultConfig);

  const updateConfiguration = async (configurations: CurrencyConfig) => {
    setLoading(true);

    try {
      const tenant = getApiUrl();

      const response = await postConfigurationUpdate({
        tenant: tenant,
        app_name: "currency_settings",
        configurations: {
          base_currency: configurations.base_currency,
          multi_currency_enabled: configurations.multi_currency_enabled,
          currencies: configurations.currencies,
        },
        is_active: configurations.multi_currency_enabled,
      });

      if (!response.ok) {
        throw new Error(`Failed to update configuration: ${response.statusText}`);
      }

      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges),
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to update configuration";

      notify({
        status: "error",
        text: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleChange = (value: string) => {
    const newEnabled = value === "active";

    setConfig(prev => ({
      ...prev,
      multi_currency_enabled: newEnabled,
    }));
  };

  const handleRateChange = (code: string, rate: string) => {
    setConfig(prev => ({
      ...prev,
      currencies: prev.currencies.map(c => (c.code === code ? { ...c, rate } : c)),
    }));
  };

  const handleSave = () => {
    updateConfiguration(config);
  };

  // Load existing configuration from API
  useEffect(() => {
    const loadConfiguration = async () => {
      try {
        const tenant = getApiUrl();

        if (!tenant) {
          setInitialLoading(false);

          return;
        }

        const response = await fetch(
          `${getAppPlatformUrl("app-configurations")}/app/get-configuration?tenant=${encodeURIComponent(tenant)}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        if (response.ok) {
          const result = await response.json();

          const currencyApp = result.find((app: any) => app.app_name === "currency_settings");

          if (currencyApp && currencyApp.configurations) {
            setConfig({
              base_currency: currencyApp.configurations.base_currency || "USD",
              multi_currency_enabled: currencyApp.configurations.multi_currency_enabled ?? false,
              currencies: currencyApp.configurations.currencies?.length
                ? currencyApp.configurations.currencies
                : [{ code: "CAD", rate: "" }],
            });
          }
        }
      } catch (error) {
        // Use defaults on error
      } finally {
        setInitialLoading(false);
      }
    };

    loadConfiguration();
  }, []);

  if (initialLoading) {
    return (
      <DetailPageLayout gridTemplateColumns={1}>
        <TopNav href={ExtensionsUrls.resolveInstalledExtensionsUrl()} title="Currency Settings" />
        <DetailPageLayout.Content>
          <DashboardCard paddingX={6}>
            <Box paddingY={6}>
              <Box display="flex" flexDirection="column" gap={4}>
                <Box
                  padding={4}
                  borderWidth={1}
                  borderColor="default1"
                  borderStyle="solid"
                  borderRadius={4}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap={3}
                  __minHeight="80px"
                >
                  <Spinner />
                  <Text size={3} color="default2">
                    Loading configuration...
                  </Text>
                </Box>
              </Box>
            </Box>
          </DashboardCard>
        </DetailPageLayout.Content>
      </DetailPageLayout>
    );
  }

  return (
    <DetailPageLayout gridTemplateColumns={1}>
      <TopNav href={ExtensionsUrls.resolveInstalledExtensionsUrl()} title="Currency Settings" />
      <DetailPageLayout.Content>
        <DashboardCard paddingX={6}>
          <Box paddingY={6}>
            <Box display="flex" flexDirection="column" gap={4}>
              {/* Base Currency (read-only) */}
              <Box
                padding={4}
                borderWidth={1}
                borderColor="default1"
                borderStyle="solid"
                borderRadius={4}
              >
                <Box display="flex" flexDirection="column" gap={2}>
                  <Text size={4} fontWeight="medium">
                    Base Currency
                  </Text>
                  <Text size={3} color="default2">
                    All prices are stored and managed in the base currency. This cannot be changed.
                  </Text>
                  <Box
                    padding={3}
                    borderWidth={1}
                    borderColor="default1"
                    borderStyle="solid"
                    borderRadius={4}
                    backgroundColor="default2"
                    display="flex"
                    alignItems="center"
                    gap={2}
                  >
                    <Text size={4} fontWeight="bold">
                      USD ($)
                    </Text>
                    <Text size={3} color="default2">
                      — United States Dollar
                    </Text>
                  </Box>
                </Box>
              </Box>

              {/* Multi-Currency Toggle */}
              <Box
                padding={4}
                borderWidth={1}
                borderColor="default1"
                borderStyle="solid"
                borderRadius={4}
              >
                <Box display="flex" flexDirection="column" gap={3}>
                  <Box display="flex" alignItems="center" gap={3}>
                    <Text size={4} fontWeight="medium">
                      Enable Multi-Currency Display
                    </Text>
                    <Switch
                      value={config.multi_currency_enabled ? "active" : "inactive"}
                      onValueChange={handleToggleChange}
                      disabled={loading}
                      __height="32px"
                      __width="fit-content"
                      padding={0.5}
                    >
                      <Switch.Item
                        id="inactive"
                        value="inactive"
                        name="multi-currency"
                        disabled={loading}
                        marginRight={0.5}
                      >
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          paddingX={2}
                          height="100%"
                        >
                          <Text size={2} fontWeight="medium">
                            Off
                          </Text>
                        </Box>
                      </Switch.Item>
                      <Switch.Item
                        id="active"
                        value="active"
                        name="multi-currency"
                        disabled={loading}
                        marginLeft={0.5}
                      >
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          paddingX={2}
                          height="100%"
                        >
                          <Text size={2} fontWeight="medium">
                            On
                          </Text>
                        </Box>
                      </Switch.Item>
                    </Switch>
                  </Box>
                  <Text size={3} color="default2">
                    {config.multi_currency_enabled
                      ? "Storefront prices will be displayed in converted currencies based on configured exchange rates."
                      : "Only USD pricing is shown on the storefront."}
                  </Text>
                </Box>
              </Box>

              {/* Exchange Rates (visible only when multi-currency is enabled) */}
              {config.multi_currency_enabled && (
                <Box
                  padding={4}
                  borderWidth={1}
                  borderColor="default1"
                  borderStyle="solid"
                  borderRadius={4}
                >
                  <Box display="flex" flexDirection="column" gap={4}>
                    <Text size={4} fontWeight="medium">
                      Exchange Rates
                    </Text>
                    <Text size={3} color="default2">
                      Set the conversion rate from USD to each target currency.
                    </Text>

                    {config.currencies.map(currency => (
                      <Box key={currency.code} display="flex" alignItems="center" gap={3}>
                        <Text size={3} fontWeight="medium" __minWidth="60px">
                          1 USD =
                        </Text>
                        <Box __width="200px">
                          <Input
                            type="number"
                            value={currency.rate}
                            onChange={e => handleRateChange(currency.code, e.target.value)}
                            disabled={loading}
                            placeholder="0.00"
                            size="small"
                          />
                        </Box>
                        <Text size={3} fontWeight="medium">
                          {currency.code}
                        </Text>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Save Button */}
              <Box display="flex" justifyContent="flex-end" marginTop={2}>
                <Button variant="primary" onClick={handleSave} disabled={loading}>
                  {loading ? "Saving..." : "Save Configuration"}
                </Button>
              </Box>
            </Box>
          </Box>
        </DashboardCard>
      </DetailPageLayout.Content>
    </DetailPageLayout>
  );
};
